// Section 24.12 import/export: a single JSON file matching the envelope. Import validates with
// the schema (and migrates) before anything touches the store. A run's shareable form
// (Section 21) is the envelope minus the snapshot; loading one replays the decision log.
import { configSnapshot, type Config, type GameState } from '../sim/api';
import { balanceCheck, completeConfig, type BalanceCheck } from './config';
import type { SaveEnvelope } from './envelope';
import { replay } from './replay';
import { decodeEnvelope } from './save';

export interface ExportFile {
  filename: string;
  text: string;
}

function slug(text: string): string {
  return text.replace(/[^a-z0-9-]+/gi, '-').replace(/^-|-$/g, '').slice(0, 40) || 'run';
}

/** Pretty-printed so a shared run is readable; imports accept any whitespace. */
export function exportFile(envelope: SaveEnvelope): ExportFile {
  const s = envelope.summary;
  const kind = envelope.snapshot === null ? 'run' : 'save';
  return {
    filename: `utopia-${slug(envelope.seed)}-y${s.year}-${s.season}-${kind}.json`,
    text: `${JSON.stringify(envelope, null, 1)}\n`,
  };
}

export interface LoadedRun {
  envelope: SaveEnvelope;
  state: GameState;
  /** The config the run continues on (the saved one, completed with any newer keys). */
  cfg: Config;
  balance: BalanceCheck;
}

/**
 * Turn an envelope into a playable state: its snapshot, or a replay of the decision log from
 * newGame(options, seed) on the saved config.
 */
export function loadEnvelope(envelope: SaveEnvelope): LoadedRun {
  const cfg = completeConfig(envelope.configSnapshot);
  const state = envelope.snapshot ?? replay(envelope, { cfg });
  return { envelope, state, cfg, balance: balanceCheck(envelope) };
}

/** Fork a run onto the current balance: replay its decisions on the current config. */
export function forkOntoCurrent(envelope: SaveEnvelope): LoadedRun {
  const cfg = configSnapshot();
  return {
    envelope,
    state: replay(envelope, { cfg }),
    cfg,
    balance: { configDiffers: false, contentDiffers: false },
  };
}

/** Parse an imported file; throws SimError('schema' | 'migration') on a bad file. */
export function importText(text: string): LoadedRun {
  return loadEnvelope(decodeEnvelope(text));
}
